import {Button, ButtonProps, Dialog} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {buildArray} from 'shared/web';

import {FilterControls} from './components/FilterControls';
import {useOfferButtonLabel} from './hooks/useOfferButtonLabel';
import {FilterFieldName, getFilterLabels} from './utils/getFilterSections';

interface FilterEditorProps {
  field: FilterFieldName;
  onApply: () => void;
  onContinue?: () => void;
  onCancel: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  hasError: boolean;
  onRetry: () => void;
  count: number | undefined;
}

export function FilterEditor(props: FilterEditorProps) {
  const offerButtonLabel = useOfferButtonLabel(props.count, props.isLoading);
  const fields = match(props.field)
    .with('model', 'make', () => ['make', 'model'] as const)
    .with('margin', 'marginPercent', () => ['margin', 'marginPercent'] as const)
    .with('address', 'radius', () => ['address', 'radius'] as const)
    .otherwise((field) => [field]);

  return (
    <Dialog
      isOpen
      closeOnOverlayClick
      title={getFilterLabels()[props.field]}
      onClose={props.onCancel}
      size={
        props.field === 'make' || props.field === 'model' || props.field === 'features'
          ? 'large'
          : 'small'
      }
      buttons={buildArray<ButtonProps>()
        .add({
          title: i18n.t('general.actions.cancel'),
          variant: 'outlined',
          size: 'small',
          onClick: props.onCancel,
        })
        .when(isNotNil(props.onContinue), {
          title: i18n.t('general.actions.saveAndContinue'),
          variant: 'outlined',
          size: 'small',
          onClick: props.onContinue,
        })
        .add({
          title: offerButtonLabel,
          size: 'small',
          onClick: props.onApply,
          isLoading: props.isLoading,
          isDisabled: props.isDisabled,
        })}
      data-testid="sourcing-filter-editor"
    >
      <Show when={props.hasError}>
        <Button title={i18n.t('general.actions.retry')} onClick={props.onRetry} />
      </Show>
      <FilterControls fields={[...fields]} isCompact />
    </Dialog>
  );
}
